/**
 * Organization Channels API Service (TASK-233 Phase 4)
 *
 * Org-scoped OUTGOING channels (v1: `outgoing_webhook`) that belong to the whole
 * company rather than a single operator. The response shape mirrors the
 * user-scoped channel (`codename, display_name, config_schema, enabled, values`)
 * so the same ChannelSection UI can render it.
 *
 * Secret fields are masked on the way out (`***` + `<name>_is_set`); a blank
 * secret on PUT never overwrites a stored one. PUT requires `users.manage`.
 */
import apiClient from './client';
import type { ChannelField } from '../../types/user';

/**
 * One org-scoped channel config, as returned by
 * `GET /api/v1/organization/channels/{codename}/`.
 */
export interface OrgChannel {
  codename: string;
  display_name: string;
  config_schema: ChannelField[];
  enabled: boolean;
  values: Record<string, unknown>;
  /**
   * Channel direction. Org channels (v1: `outgoing_webhook`) are always
   * outgoing, but the field is optional because the backend response may omit
   * it; the consumer (IntegrationSettings) sets it explicitly when missing.
   */
  direction?: 'incoming' | 'outgoing';
}

export const orgChannelsApi = {
  /**
   * Fetch one org channel's catalog entry merged with the org's stored config.
   *
   * GET /api/v1/organization/channels/{codename}/
   */
  getOrgChannel: async (codename: string): Promise<OrgChannel> => {
    const response = await apiClient.get<OrgChannel>(
      `/api/v1/organization/channels/${codename}/`
    );
    return response.data;
  },

  /**
   * Upsert the org's config for a channel.
   *
   * PUT /api/v1/organization/channels/{codename}/
   * body: { enabled, values }
   */
  updateOrgChannel: async (
    codename: string,
    body: { enabled: boolean; values: Record<string, unknown> }
  ): Promise<OrgChannel> => {
    const response = await apiClient.put<OrgChannel>(
      `/api/v1/organization/channels/${codename}/`,
      body
    );
    return response.data;
  },
};

export default orgChannelsApi;
